import { Injectable } from '@angular/core'
import { OfferSelectionCardModel } from '@flocasts/experience-service-types'
import {
  ReplaySubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap
} from 'rxjs'
import { PartialsDataService } from 'src/app/singleton-services/partials/partials-data.service'
import { isOfferListPartialModel, notNullOrUndefined } from '../shared/functions/type-guards'
import { CouponService } from '../shared/services/coupon.service'
import { ProductService } from '../shared/services/product.service'
import { VerticalService } from './vertical.service'
import { SiteIds } from '../shared/models/site.model'
import { ExperimentationService } from '../shared/experimentation/experimentation.service'
import {
  EXP_NEW_PLANS_PAGE_ID,
  EXP_NEW_PLANS_PAGE_VARIATION_1,
  EXP_NEW_PLANS_PAGE_TOP_VERTICALS_ID,
  EXP_NEW_PLANS_PAGE_TOP_VERTICALS_VARIATION_1,
  EXP_FLOCOLLEGE_FAMILY_PLAN_ID,
  EXP_FLOCOLLEGE_FAMILY_PLAN_VARIATION,
  EXP_FLOHOCKEY_FAMILY_PLAN_ID,
  EXP_FLOHOCKEY_FAMILY_PLAN_VARIATION,
  FEAT_UNIFIED_LAYOUT_ID,
  FEAT_UNIFIED_LAYOUT_VARIATION,
  FEAT_GWP_ID,
  FEAT_GWP_VARIATION
} from '../shared/experimentation/experiment.model'

@Injectable({
  providedIn: 'root'
})
export class OffersService {
  constructor(
    private readonly couponService: CouponService,
    private readonly partialsDataService: PartialsDataService,
    private readonly verticalService: VerticalService,
    private readonly productService: ProductService,
    private readonly experimentService: ExperimentationService
  ) {}

  public isIn2025PlansPageTopVerticalsVariation$ = this.experimentService.isInVariation(
    EXP_NEW_PLANS_PAGE_TOP_VERTICALS_ID,
    EXP_NEW_PLANS_PAGE_TOP_VERTICALS_VARIATION_1
  )

  public isFloHockeyFamilyPlan$ = this.experimentService.isInVariation(
    EXP_FLOHOCKEY_FAMILY_PLAN_ID,
    EXP_FLOHOCKEY_FAMILY_PLAN_VARIATION
  )

  public isFloCollegeFamilyPlan$ = this.experimentService.isInVariation(
    EXP_FLOCOLLEGE_FAMILY_PLAN_ID,
    EXP_FLOCOLLEGE_FAMILY_PLAN_VARIATION
  )

  public isIn2025PlansPageVariation$ = this.experimentService.isInVariation(
    EXP_NEW_PLANS_PAGE_ID,
    EXP_NEW_PLANS_PAGE_VARIATION_1
  )

  public isUnifiedLayout$ = this.experimentService.isInVariation(FEAT_UNIFIED_LAYOUT_ID, FEAT_UNIFIED_LAYOUT_VARIATION)

  public isVarsityGiftWithPurchase$ = this.experimentService.isInVariation(FEAT_GWP_ID, FEAT_GWP_VARIATION)

  public offers$ = combineLatest([
    this.verticalService.siteId$,
    this.couponService.coupon$,
    this.productService.specialPricingValue$,
    this.isIn2025PlansPageVariation$,
    this.isIn2025PlansPageTopVerticalsVariation$,
    this.isFloHockeyFamilyPlan$,
    this.isFloCollegeFamilyPlan$,
    this.isUnifiedLayout$,
    this.isVarsityGiftWithPurchase$
  ]).pipe(
    switchMap(
      ([
        siteId,
        coupon,
        specialPricingValue,
        isIn2025ForRacing,
        isTopVerticalsVariation,
        isFloHockeyFamilyPlan,
        isFloCollegeFamilyPlan,
        isUnifiedLayout,
        isVarsityGiftWithPurchase
      ]) => {
        // TODO: CXP-10174 determine special pricing or new plan feature flag for bff
        const specialPrice = this.determineSpecialPrice(
          specialPricingValue,
          siteId,
          isFloHockeyFamilyPlan,
          isFloCollegeFamilyPlan,
          isVarsityGiftWithPurchase
        )

        const shouldShowFamilyPlan =
          (siteId === SiteIds.FLOCOLLEGE && isFloCollegeFamilyPlan) ||
          (siteId === SiteIds.FLOHOCKEY && isFloHockeyFamilyPlan)
        return this.partialsDataService.getOffersListPartial(
          siteId,
          coupon,
          specialPrice,
          isTopVerticalsVariation || isIn2025ForRacing || isUnifiedLayout,
          shouldShowFamilyPlan
        )
      }
    ),
    filter(notNullOrUndefined),
    filter(isOfferListPartialModel),
    map(res => res?.data),
    distinctUntilChanged(),
    shareReplay(1)
  )

  public offersLength$ = this.offers$.pipe(map(offers => offers.length))

  // TODO: Refactor the two ReplaySubjects below into one variable
  // that can keep track of the selectedOffer values on the funnel
  // and the change offer flow

  /**
   * This variable keeps track of the user's currently selected offer in the funnel,
   * and will update any time a user selects a new offer on the /plans route.
   *
   * Below we are piping off of the offers$ stream and using a switchMap because
   * we want the value of selectedOffer$ to default to the first value of the
   * offers array. So this value is dependent on the offers stream's initial emit.
   */
  private selectedOffer = new ReplaySubject<OfferSelectionCardModel | undefined>()
  private _selectedOffer$ = this.selectedOffer.asObservable()
  public selectedOffer$ = this.offers$.pipe(
    switchMap(offers => this._selectedOffer$.pipe(startWith(offers.find(offer => offer.defaultSelection)))),
    shareReplay(1)
  )

  public updateSelectedOffer(offer: OfferSelectionCardModel) {
    this.selectedOffer.next(offer)
  }

  /**
   * This variable keeps track of the user's selected offer on the /account/chang-offer/plans route,
   * and will update any time a user selects a new offer on the /account/change-offer/plans route.
   */
  private selectedChangeOffer = new ReplaySubject<OfferSelectionCardModel | undefined>()
  private _selectedChangeOffer$ = this.selectedChangeOffer.asObservable()
  public selectedChangeOffer$ = this.offers$.pipe(
    switchMap(offers => this._selectedChangeOffer$.pipe(startWith(offers.find(offer => offer.defaultSelection)))),
    shareReplay(1)
  )

  public updateSelectedChangeOffer(offer: OfferSelectionCardModel) {
    this.selectedChangeOffer.next(offer)
  }

  /**
   * Special pricing is used for conference / partner deals as well as pricing experimentation and unique
   * pricing per vertical.
   *
   * @param specialPricingValue
   * @param siteId
   * @param isNewRacingPriceIncreaseVariation CXP-8152 FloRacing Price Increase Holdout Variation
   * @param isPricingVariationA CXP-10174 New Plans Page Variation A
   * @param isPricingVariationB CXP-10174 New Plans Page Variation B with family plan
   * @param isFloHockeyFamilyPlan Whether user is in FloHockey family plan experiment
   * @param isFloCollegeFamilyPlan Whether user is in FloCollege family plan experiment
   * @param isVarsityGiftWithPurchase Whether user is in Varsity gift with purchase experiment
   * @private
   */
  private determineSpecialPrice(
    specialPricingValue: string | undefined,
    siteId: number,
    isFloHockeyFamilyPlan: boolean,
    isFloCollegeFamilyPlan: boolean,
    isVarsityGiftWithPurchase: boolean
  ): string | undefined {
    let specialPrice = specialPricingValue

    if (specialPricingValue === 'conf-partner') {
      // Conference partner special pricing takes precedence
      specialPrice = 'conf-partner'
    } else if (siteId === SiteIds.FLOFOOTBALL) {
      // CXP-4067 Football in new architecture - different monthly price from standard
      specialPrice = 'FloFootball Monthly'
    } else if (siteId === SiteIds.FLORACING) {
      // Racing Price is pricier than standard
      specialPrice = 'fc3ca7db-9bc0-4178-bbc2-17512e1cca25'
    } else if (siteId === SiteIds.FLOHOCKEY && isFloHockeyFamilyPlan) {
      // Hockey family plan
      specialPrice = 'f4372793-9fc8-4956-9182-8099248d288d'
    } else if (siteId === SiteIds.FLOCOLLEGE) {
      if (isFloCollegeFamilyPlan) {
        // College family plan
        specialPrice = 'c91b5a05-ad7a-40fc-a79e-be7bfa563a0a'
      } else {
        // different monthly price from standard
        specialPrice = 'a87b3b68-2980-4e0c-bf34-b6421237e7fd'
      }
    } else if (siteId === SiteIds.VARSITY && isVarsityGiftWithPurchase) {
      specialPrice = '7fe52f46-429d-4bba-9606-03d408520cb1'
    }
    return specialPrice
  }
}
